import React from 'react';
import { Grid, GridProps } from '@material-ui/core';
import { Skeleton } from '#root/components/ui/primitives';
import { GuveryPaper } from '#root/ui-components/cards';

export interface SkeletonListProps {
  count?: number;
  height?: string | number;
  variant?: 'rect' | 'text' | 'circle';
  animation?: 'pulse' | 'wave' | false;
  spacing?: GridProps['spacing'];
  containerProps?: GridProps;
  skeletonProps?: any;
  renderSkeleton?: () => React.ReactNode;
  usePaper?: boolean;
  paperProps?: any;
}

/**
 * Componente reutilizable para mostrar skeleton loaders en listas.
 * Elimina ~225 líneas de código duplicado en 15+ archivos.
 *
 * @example
 * // Uso básico
 * <SkeletonList count={5} height={263} />
 *
 * @example
 * // Con variante predefinida
 * <CardSkeletonList count={3} />
 */
export const SkeletonList: React.FC<SkeletonListProps> = ({
  count = 5,
  height = 220,
  variant = 'rect',
  animation = 'wave',
  spacing = 2,
  containerProps = {},
  skeletonProps = {},
  renderSkeleton,
  usePaper = true,
  paperProps = {},
}) => {
  const renderSkeletonItem = () => {
    if (renderSkeleton) {
      return renderSkeleton();
    }

    const skeleton = (
      <Skeleton
        variant={variant}
        height={height}
        animation={animation}
        {...skeletonProps}
      />
    );

    return usePaper ? (
      <GuveryPaper disableHover disableElevation {...paperProps}>
        {skeleton}
      </GuveryPaper>
    ) : (
      skeleton
    );
  };

  return (
    <Grid container spacing={spacing} {...containerProps}>
      {Array.from(new Array(count)).map((_, index) => (
        <Grid item key={`skeleton-${index}`} xs={12}>
          {renderSkeletonItem()}
        </Grid>
      ))}
    </Grid>
  );
};

// Variantes predefinidas para casos comunes

/**
 * SkeletonList con altura de card (263px).
 * Usado en TravelerDeals, UserOrders, etc.
 */
export const CardSkeletonList: React.FC<Omit<SkeletonListProps, 'height'>> = (props) => (
  <SkeletonList height={220} {...props} />
);

/**
 * SkeletonList con altura de list item (72px) sin Paper wrapper.
 * Usado en listados compactos.
 */
export const ListItemSkeletonList: React.FC<Omit<SkeletonListProps, 'height' | 'usePaper'>> = (
  props,
) => <SkeletonList height={72} usePaper={false} {...props} />;

/**
 * SkeletonList con renderizado personalizado en formato tabla (4 columnas).
 * Usado en tablas con grid layout.
 */
export const TableRowSkeletonList: React.FC<Omit<SkeletonListProps, 'renderSkeleton'>> = (
  props,
) => (
  <SkeletonList
    {...props}
    renderSkeleton={() => (
      <Grid container spacing={1}>
        <Grid item xs={3}>
          <Skeleton variant="text" />
        </Grid>
        <Grid item xs={3}>
          <Skeleton variant="text" />
        </Grid>
        <Grid item xs={3}>
          <Skeleton variant="text" />
        </Grid>
        <Grid item xs={3}>
          <Skeleton variant="text" />
        </Grid>
      </Grid>
    )}
  />
);
